// Audio capture service for translation
// Captures microphone audio and sends to server for translation
// Uses AudioWorkletNode for better performance (replaces deprecated ScriptProcessorNode)
// Includes auto-calibrating VAD (Voice Activity Detection)
// Includes AI-powered noise suppression (RNNoise)

import { getVAD, resetVAD } from './vad.js';
import { getAudioProcessor, resetAudioProcessor } from './audioProcessor.js';

let audioContext = null;
let mediaStreamSource = null;
let workletNode = null;
let isCapturing = false;
let vad = null;
let vadEnabled = true; // Can be disabled for testing
let currentSocket = null;
let packetCount = 0;
let speechPacketCount = 0;
let audioProcessor = null; // AI audio processor (noise suppression)
let noiseSuppressionEnabled = true; // Can be toggled at runtime

// Prefix padding - circular buffer to capture audio BEFORE speech detected
const PREFIX_BUFFER_MS = 300; // Keep 300ms of audio before speech
const SAMPLES_PER_PACKET = 4096;
const SAMPLE_RATE = 24000;
const PACKETS_TO_BUFFER = Math.ceil((PREFIX_BUFFER_MS / 1000) * SAMPLE_RATE / SAMPLES_PER_PACKET);
let prefixBuffer = []; // Circular buffer of recent audio packets
let wasSpeaking = false; // Track speech state transitions

// VAD event callbacks (can be set externally)
export let onSpeechStart = null;
export let onSpeechEnd = null;
export let onVADCalibrated = null;
export let onVADEnergyUpdate = null;

export function setVADEnabled(enabled) {
  vadEnabled = enabled;
  console.log(`[AudioCapture] VAD ${enabled ? 'enabled' : 'disabled'}`);
}

export function setNoiseSuppressionEnabled(enabled) {
  noiseSuppressionEnabled = enabled;
  if (audioProcessor) {
    audioProcessor.setNoiseSuppressionEnabled(enabled);
  }
  console.log(`[AudioCapture] Noise suppression ${enabled ? 'enabled' : 'disabled'}`);
}

export function isNoiseSuppressionActive() {
  return audioProcessor?.isNoiseSuppressionActive() || false;
}

export async function startAudioCapture(stream, socket, options = {}) {
  if (isCapturing) return;

  try {
    // Initialize AI audio processor (noise suppression)
    audioProcessor = getAudioProcessor();
    if (!audioProcessor.isReady()) {
      console.log('[AudioCapture] Initializing AI audio processor...');
      await audioProcessor.initialize({
        enableNoiseSuppression: options.enableNoiseSuppression !== false,
      });
    }

    audioContext = new (window.AudioContext || window.webkitAudioContext)({
      sampleRate: 24000, // OpenAI Realtime expects 24kHz
    });

    console.log('AudioContext created with sample rate:', audioContext.sampleRate);
    console.log('[AudioCapture] Noise suppression active:', audioProcessor.isNoiseSuppressionActive());

    currentSocket = socket;
    packetCount = 0;
    speechPacketCount = 0;
    prefixBuffer = []; // Reset prefix buffer
    wasSpeaking = false;

    // Initialize VAD
    // noiseMargin 1.4 = more sensitive (better for noisy environments like bars)
    // Lower value = more sensitive but more false positives
    // hangoverTime 1000ms = allows natural pauses without fragmenting speech
    vad = getVAD({
      calibrationDuration: options.vadCalibrationDuration || 1500,
      noiseMargin: options.vadNoiseMargin || 1.4,
      hangoverTime: options.vadHangoverTime || 1000,
    });

    // Set up VAD callbacks with diagnostic logging
    let speechStartPackets = 0;
    vad.onSpeechStart = () => {
      speechStartPackets = speechPacketCount;
      console.log(`[VAD] Speech started (total packets so far: ${speechPacketCount})`);
      if (onSpeechStart) onSpeechStart();
    };
    vad.onSpeechEnd = () => {
      const packetsSent = speechPacketCount - speechStartPackets;
      console.log(`[VAD] Speech ended - sent ${packetsSent} packets this utterance`);
      if (packetsSent === 0) {
        console.warn('[VAD] WARNING: Speech detected but 0 packets sent!');
      } else if (packetsSent < 5) {
        console.warn(`[VAD] WARNING: Very few packets sent (${packetsSent}) - might be too short for STT`);
      }
      // Notify server that speech ended - triggers STT flush for immediate translation
      if (currentSocket && currentSocket.connected) {
        currentSocket.emit('speech-end');
        console.log('[VAD] Emitted speech-end to server');
      }
      if (onSpeechEnd) onSpeechEnd();
    };
    vad.onCalibrationComplete = (data) => {
      console.log('[AudioCapture] VAD calibrated:', data);
      if (onVADCalibrated) onVADCalibrated(data);
    };
    vad.onEnergyUpdate = (data) => {
      if (onVADEnergyUpdate) onVADEnergyUpdate(data);
    };

    // Start VAD calibration
    vad.start();

    mediaStreamSource = audioContext.createMediaStreamSource(stream);

    // Load AudioWorklet module
    try {
      await audioContext.audioWorklet.addModule('/audio-worklet-processor.js');

      // Create AudioWorkletNode
      workletNode = new AudioWorkletNode(audioContext, 'audio-capture-processor');

      // Handle messages from worklet
      workletNode.port.onmessage = (event) => {
        if (!isCapturing) return;

        if (event.data.type === 'audio') {
          let inputData = event.data.buffer;

          // Apply AI noise suppression (always active when enabled)
          if (audioProcessor?.isReady() && noiseSuppressionEnabled) {
            inputData = audioProcessor.process(inputData, audioContext.sampleRate);
          }

          // Always add to prefix buffer (circular) - keep audio from BEFORE speech
          const pcm16ForBuffer = float32ToPcm16(inputData);
          prefixBuffer.push(pcm16ForBuffer);
          if (prefixBuffer.length > PACKETS_TO_BUFFER) {
            prefixBuffer.shift(); // Remove oldest packet
          }

          // Process through VAD (uses cleaned audio for better detection)
          const isSpeech = vadEnabled ? vad.process(inputData) : true;

          // Detect speech START transition - send prefix buffer first
          if (isSpeech && !wasSpeaking) {
            console.log(`[PREFIX] Speech started - sending ${prefixBuffer.length} buffered packets (${Math.round(prefixBuffer.length * SAMPLES_PER_PACKET / SAMPLE_RATE * 1000)}ms)`);
            // Send all buffered audio BEFORE the current packet
            for (let i = 0; i < prefixBuffer.length - 1; i++) {
              currentSocket.emit('audio-for-translation', {
                audio: arrayBufferToBase64(prefixBuffer[i].buffer),
              });
              speechPacketCount++;
            }
            wasSpeaking = true;
          }

          // Send ALL audio to server - let Deepgram handle VAD with 300ms endpointing
          // Client VAD is only used for UI indicators, not for filtering audio
          const pcm16 = float32ToPcm16(inputData);
          currentSocket.emit('audio-for-translation', {
            audio: arrayBufferToBase64(pcm16.buffer),
          });
          speechPacketCount++;

          // Track speech state for UI (but don't filter)
          if (!isSpeech && wasSpeaking) {
            wasSpeaking = false;
          }

          packetCount++;
          if (packetCount % 50 === 0) {
            console.log(`Audio: ${packetCount} total, ${speechPacketCount} speech packets sent`);
          }
        }
      };

      mediaStreamSource.connect(workletNode);
      workletNode.connect(audioContext.destination);

      console.log('Audio capture started using AudioWorkletNode (with VAD)');
    } catch (workletError) {
      // Fallback to ScriptProcessorNode if AudioWorklet not supported
      console.warn('AudioWorklet not supported, falling back to ScriptProcessorNode:', workletError);
      setupScriptProcessor(socket);
    }

    isCapturing = true;
  } catch (error) {
    console.error('Failed to start audio capture:', error);
  }
}

// Fallback for browsers that don't support AudioWorklet
function setupScriptProcessor(socket) {
  const scriptProcessor = audioContext.createScriptProcessor(4096, 1, 1);

  scriptProcessor.onaudioprocess = (event) => {
    if (!isCapturing) return;

    let inputData = event.inputBuffer.getChannelData(0);

    // Apply AI noise suppression (always active when enabled)
    if (audioProcessor?.isReady() && noiseSuppressionEnabled) {
      inputData = audioProcessor.process(inputData, audioContext.sampleRate);
    }

    // Always add to prefix buffer (circular) - keep audio from BEFORE speech
    const pcm16ForBuffer = float32ToPcm16(inputData);
    prefixBuffer.push(pcm16ForBuffer);
    if (prefixBuffer.length > PACKETS_TO_BUFFER) {
      prefixBuffer.shift();
    }

    // Process through VAD (uses cleaned audio for better detection)
    const isSpeech = vadEnabled ? vad.process(inputData) : true;

    // Detect speech START transition - send prefix buffer first
    if (isSpeech && !wasSpeaking) {
      console.log(`[PREFIX] Speech started - sending ${prefixBuffer.length} buffered packets`);
      for (let i = 0; i < prefixBuffer.length - 1; i++) {
        socket.emit('audio-for-translation', {
          audio: arrayBufferToBase64(prefixBuffer[i].buffer),
        });
        speechPacketCount++;
      }
      wasSpeaking = true;
    }

    // Only send audio when speech is detected (or VAD disabled)
    if (isSpeech) {
      // Convert Float32 to Int16 PCM
      const pcm16 = float32ToPcm16(inputData);

      // Send to server
      socket.emit('audio-for-translation', {
        audio: arrayBufferToBase64(pcm16.buffer),
      });

      speechPacketCount++;
    } else {
      if (wasSpeaking) {
        wasSpeaking = false;
      }
    }

    packetCount++;
    if (packetCount % 50 === 0) {
      console.log(`Audio: ${packetCount} total, ${speechPacketCount} speech packets sent`);
    }
  };

  mediaStreamSource.connect(scriptProcessor);
  scriptProcessor.connect(audioContext.destination);

  // Store reference for cleanup
  workletNode = scriptProcessor;

  console.log('Audio capture started using ScriptProcessorNode fallback (with VAD)');
}

export function stopAudioCapture() {
  isCapturing = false;

  // Reset prefix buffer
  prefixBuffer = [];
  wasSpeaking = false;

  // Stop VAD
  if (vad) {
    vad.stop();
    vad = null;
  }
  resetVAD();

  // Note: We don't destroy the audio processor here because
  // noise suppression should remain available for reuse.
  // Call resetAudioProcessor() explicitly if you want to free resources.

  if (workletNode) {
    workletNode.disconnect();
    workletNode = null;
  }

  if (mediaStreamSource) {
    mediaStreamSource.disconnect();
    mediaStreamSource = null;
  }

  if (audioContext && audioContext.state !== 'closed') {
    audioContext.close();
    audioContext = null;
  }

  currentSocket = null;
  console.log('Audio capture stopped');
  console.log('[AudioCapture] Noise suppression provider still available for reuse');
}

/**
 * Recalibrate VAD (e.g., when environment changes)
 */
export function recalibrateVAD() {
  if (vad) {
    vad.recalibrate();
  }
}

/**
 * Get current VAD state
 */
export function getVADState() {
  return vad ? vad.getState() : null;
}

// Convert Float32Array to Int16Array (PCM16)
function float32ToPcm16(float32Array) {
  const pcm16 = new Int16Array(float32Array.length);
  for (let i = 0; i < float32Array.length; i++) {
    // Clamp and convert
    const s = Math.max(-1, Math.min(1, float32Array[i]));
    pcm16[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
  }
  return pcm16;
}

// Convert ArrayBuffer to Base64
function arrayBufferToBase64(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

// Play translated audio received from server with proper queuing
let playbackContext = null;
let nextPlayTime = 0;
let isFirstChunk = true;
let audioQueue = []; // Queue for audio while context is suspended
let isProcessingQueue = false;

// Pre-initialize playback context (call on user interaction like joining meeting)
export function initPlayback() {
  if (!playbackContext || playbackContext.state === 'closed') {
    playbackContext = new (window.AudioContext || window.webkitAudioContext)({
      sampleRate: 24000,
    });
    console.log('[AudioPlayback] Pre-initialized AudioContext, state:', playbackContext.state);
  }
  // Resume immediately if suspended (works after user interaction)
  if (playbackContext.state === 'suspended') {
    playbackContext.resume().then(() => {
      console.log('[AudioPlayback] AudioContext resumed after user interaction');
    }).catch(e => {
      console.error('[AudioPlayback] Failed to resume:', e);
    });
  }
  return playbackContext.state;
}

export function playTranslatedAudio(base64Audio) {
  console.log('[AudioPlayback] Received audio chunk, length:', base64Audio.length);

  if (!playbackContext || playbackContext.state === 'closed') {
    playbackContext = new (window.AudioContext || window.webkitAudioContext)({
      sampleRate: 24000,
    });
    nextPlayTime = 0;
    isFirstChunk = true;
    console.log('[AudioPlayback] Created new AudioContext, state:', playbackContext.state);
  }

  // If context is suspended, queue audio and try to resume
  if (playbackContext.state === 'suspended') {
    console.log('[AudioPlayback] Context suspended, queueing audio and resuming...');
    audioQueue.push(base64Audio);

    playbackContext.resume().then(() => {
      console.log('[AudioPlayback] Context resumed, processing queue...');
      processAudioQueue();
    }).catch(e => {
      console.error('[AudioPlayback] Resume failed:', e);
    });
    return;
  }

  // Play directly if context is running
  playAudioChunk(base64Audio);
}

function processAudioQueue() {
  if (isProcessingQueue || audioQueue.length === 0) return;
  isProcessingQueue = true;

  while (audioQueue.length > 0) {
    const chunk = audioQueue.shift();
    playAudioChunk(chunk);
  }

  isProcessingQueue = false;
}

function playAudioChunk(base64Audio) {
  try {
    // Decode base64 to ArrayBuffer
    const binaryString = atob(base64Audio);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }

    // Convert PCM16 to Float32
    const pcm16 = new Int16Array(bytes.buffer);
    const float32 = new Float32Array(pcm16.length);
    for (let i = 0; i < pcm16.length; i++) {
      float32[i] = pcm16[i] / 0x8000;
    }

    // Create audio buffer
    const audioBuffer = playbackContext.createBuffer(1, float32.length, 24000);
    audioBuffer.getChannelData(0).set(float32);

    // Schedule playback in sequence
    const source = playbackContext.createBufferSource();
    source.buffer = audioBuffer;
    source.connect(playbackContext.destination);

    const currentTime = playbackContext.currentTime;

    if (isFirstChunk || nextPlayTime < currentTime) {
      // First chunk or we've fallen behind - start immediately with small buffer
      nextPlayTime = currentTime + 0.05; // 50ms initial buffer
      isFirstChunk = false;
      console.log('[AudioPlayback] Starting playback at:', nextPlayTime);
    }

    source.start(nextPlayTime);
    nextPlayTime += audioBuffer.duration;
  } catch (e) {
    console.error('[AudioPlayback] Error playing chunk:', e);
  }
}

export function resetPlayback() {
  nextPlayTime = 0;
  isFirstChunk = true;
  audioQueue = [];
}

export function stopPlayback() {
  if (playbackContext && playbackContext.state !== 'closed') {
    playbackContext.close();
    playbackContext = null;
  }
  nextPlayTime = 0;
  isFirstChunk = true;
  audioQueue = [];
}
